import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';

interface Props<T> {
  /** API path that returns the choices; refetched whenever it changes. */
  readonly endpoint: string;
  readonly idField: keyof T & string;
  readonly optionLabel: (row: T) => string;
  readonly value: string;
  /** Gets the picked id and the label it was shown with ('' when cleared). */
  readonly onPick: (id: string, label: string) => void;
  readonly label: string;
  readonly testId?: string;
  readonly emptyText?: string;
  readonly disabled?: boolean;
}

/**
 * A select fed straight from an API instead of asking the user to type a uuid.
 *
 * Refetches when the window regains focus so a record created in another tab —
 * or on another screen — shows up without a reload.
 */
export function RecordPicker<T extends object>({
  endpoint,
  idField,
  optionLabel,
  value,
  onPick,
  label,
  testId = 'record-picker',
  emptyText = 'Nothing available yet',
  disabled = false,
}: Props<T>): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(() => {
    // An empty endpoint means the parent has not resolved it yet.
    if (!endpoint) { setRows([]); return Promise.resolve(); }
    setLoading(true);
    setError(null);
    return apiRequest<unknown>(endpoint, { token })
      .then((r) => {
        // Endpoints in this codebase return a bare array, { data }, { items }
        // or (M04) { rows }.
        const list = Array.isArray(r)
          ? r
          : ((r as { data?: T[]; items?: T[] })?.data ??
             (r as { items?: T[] })?.items ??
             (r as { rows?: T[] })?.rows ??
             []);
        setRows(list as T[]);
      })
      .catch((e) => { setRows([]); setError((e as Error).message); })
      .finally(() => setLoading(false));
  }, [endpoint, token]);

  useEffect(() => {
    let alive = true;
    void fetchRows();
    const onFocus = () => { if (alive) void fetchRows(); };
    window.addEventListener('focus', onFocus);
    return () => { alive = false; window.removeEventListener('focus', onFocus); };
  }, [fetchRows]);

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
      <label htmlFor={testId}>{label}</label>
      <select
        id={testId}
        data-testid={testId}
        value={value}
        onChange={(e) => {
          const row = rows.find((r) => String(r[idField]) === e.target.value);
          onPick(e.target.value, row ? optionLabel(row) : '');
        }}
        disabled={disabled}
        style={{ flex: 1, minWidth: 240, padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default, #d0d7de)' }}
      >
        <option value="">
          {loading ? 'Loading…' : rows.length === 0 ? emptyText : 'Select…'}
        </option>
        {rows.map((r) => (
          <option key={String(r[idField])} value={String(r[idField])}>{optionLabel(r)}</option>
        ))}
      </select>
      {error ? <span role="alert" style={{ color: 'var(--color-danger, red)' }}>{error}</span> : null}
    </div>
  );
}
